import React, { useId, useRef } from 'react';
import { LIBRARY_TYPES, LIBRARY_TYPE_ORDER } from '../../../models/LibraryModel';
import LibraryDialog from './LibraryDialog';
import LibraryDropZone from './LibraryDropZone';
import Select from '../shared/Select';
import './AddDocumentDialog.css';

/**
 * Add document (PRD-1606): a name, a type and one file, all required. Nothing
 * is parsed; the extension is checked and the size capped. Every keystroke
 * goes to the model's draft, so a part-filled form survives being dismissed.
 * Focus starts on the name; the shell handles the rest (see LibraryDialog).
 */
function AddDocumentDialog({ draft, defaultType, opener, fallbackFocus, onDraftChange, onSave, onClose, onShowNotification }) {
  const ids = useId();
  const nameRef = useRef(null);
  const type = draft.type || defaultType;
  const canSave = !!(draft.name.trim() && draft.file);

  const save = () => {
    if (!canSave) return;
    onSave({ name: draft.name, type, file: draft.file });
  };

  return (
    <LibraryDialog title="Add document" initialFocus={nameRef} opener={opener} fallbackFocus={fallbackFocus} onClose={onClose}>
      <div className="resui-field library-form-group">
        <label className="resui-field-label" htmlFor={`${ids}-name`}>
          Document name
          <span className="resui-field-required" aria-hidden="true">*</span>
          <span className="library-sr-only">required</span>
        </label>
        <input
          ref={nameRef}
          id={`${ids}-name`}
          type="text"
          className="resui-input"
          maxLength={80}
          placeholder="e.g. Bridge Command Reference Key"
          value={draft.name}
          onChange={(e) => onDraftChange({ name: e.target.value })}
        />
      </div>

      <div className="resui-field library-form-group">
        <label className="resui-field-label" htmlFor={`${ids}-type`}>
          Type
          <span className="resui-field-required" aria-hidden="true">*</span>
          <span className="library-sr-only">required</span>
        </label>
        <Select
          id={`${ids}-type`}
          className="resui-select-field--block"
          value={type}
          describedBy={`${ids}-type-hint`}
          options={LIBRARY_TYPE_ORDER.map((key) => ({ value: key, label: LIBRARY_TYPES[key].label }))}
          onChange={(next) => onDraftChange({ type: next })}
        />
        <p className="resui-field-helper library-form-hint" id={`${ids}-type-hint`}>{LIBRARY_TYPES[type].hint}</p>
      </div>

      <div className="resui-field library-form-group">
        <span className="resui-field-label" id={`${ids}-file`}>
          File
          <span className="resui-field-required" aria-hidden="true">*</span>
          <span className="library-sr-only">required</span>
        </span>
        <LibraryDropZone
          file={draft.file}
          labelId={`${ids}-file`}
          onFile={(file) => onDraftChange({ file })}
          onClear={() => onDraftChange({ file: null })}
          onProblem={(problem) => onShowNotification?.(problem, 'alert')}
        />
      </div>

      <div className="library-dialog-actions">
        <button type="button" className="resui-btn resui-btn--secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="resui-btn resui-btn--primary" disabled={!canSave} onClick={save}>
          Save document
        </button>
      </div>
    </LibraryDialog>
  );
}

export default AddDocumentDialog;
